import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Check, ChevronDown } from 'lucide-react';
import { useUser } from 'context/user/User';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import { actorHasOwnPanel } from 'helpers/dashboardAccess';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import { enterProducerPanel, leaveToOwnContext } from './enterProducerPanel';

/**
 * Troca de painel no header: o da própria pessoa ("Pessoal") ou o de cada
 * produtor em cuja equipe ela entrou. Escolher um produtor abre o painel dele
 * como membro, só com o que as equipes liberaram; "Pessoal" volta ao próprio.
 * Quem não tem equipe nenhuma não vê o seletor.
 */
export default function PanelSwitcher() {
    const { user } = useUser();
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    const accesses = user?.producer_panel_access || [];
    const actingAs = user?.acting_as || null;

    useEffect(() => {
        if (!open) return undefined;

        function closeOnOutsideClick(event) {
            if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
        }

        function closeOnEscape(event) {
            if (event.key === 'Escape') setOpen(false);
        }

        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);

        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [open]);

    if (!accesses.length) return null;

    // Dentro do painel de um produtor `user` é o produtor; quem entrou é o membro.
    const hasOwnPanel = actorHasOwnPanel(user);
    const ownName = actingAs ? actingAs.member?.first_name : user?.first_name;
    const ownAvatar = actingAs ? actingAs.member?.avatar : user?.avatar;
    const currentAccess = actingAs
        ? accesses.find((access) => String(access.producer_id) === String(actingAs.producer_id))
        : null;

    const current = currentAccess
        ? { name: currentAccess.name, avatar: currentAccess.avatar }
        : { name: 'Pessoal', avatar: ownAvatar };

    function choose(action) {
        setOpen(false);
        action();
    }

    return (
        <Root ref={rootRef}>
            <Trigger
                type='button'
                aria-haspopup='menu'
                aria-expanded={open}
                aria-label={`Painel atual: ${current.name}. Trocar de painel`}
                onClick={() => setOpen((value) => !value)}
            >
                <DynamicProfileImage
                    word={current.name}
                    img={current.avatar}
                    size='24px'
                    variant='rounded'
                    textSize='small'
                    bgColor={currentAccess ? colors.occasionalPurple : colors.secondary}
                />
                <CurrentName>{current.name}</CurrentName>
                <ChevronDown size={16} color={colors.night} aria-hidden='true' />
            </Trigger>

            {open && (
                <Menu role='menu' aria-label='Trocar de painel'>
                    <Option
                        type='button'
                        role='menuitemradio'
                        aria-checked={!currentAccess}
                        onClick={() => choose(currentAccess ? () => leaveToOwnContext(user) : () => {})}
                    >
                        <DynamicProfileImage
                            word={ownName || 'P'}
                            img={ownAvatar}
                            size='28px'
                            variant='rounded'
                            textSize='small'
                        />
                        <OptionText>
                            <OptionName>Pessoal</OptionName>
                            <OptionHint>{hasOwnPanel ? 'Seu painel de produtor' : 'Seus ingressos e eventos'}</OptionHint>
                        </OptionText>
                        {!currentAccess && <Check size={16} color={panelAccent(colors.occasionalPurple)} aria-hidden='true' />}
                    </Option>

                    <Caption>Equipes</Caption>

                    {accesses.map((access) => {
                        const active = String(actingAs?.producer_id) === String(access.producer_id);
                        const teams = (access.teams || []).map((team) => team.name).join(', ');

                        return (
                            <Option
                                key={access.producer_id}
                                type='button'
                                role='menuitemradio'
                                aria-checked={active}
                                onClick={() => choose(active ? () => {} : () => enterProducerPanel(access))}
                            >
                                <DynamicProfileImage
                                    word={access.name}
                                    img={access.avatar}
                                    size='28px'
                                    variant='rounded'
                                    textSize='small'
                                    bgColor={colors.occasionalPurple}
                                />
                                <OptionText>
                                    <OptionName>{access.name}</OptionName>
                                    {teams && <OptionHint>{teams}</OptionHint>}
                                </OptionText>
                                {active && <Check size={16} color={panelAccent(colors.occasionalPurple)} aria-hidden='true' />}
                            </Option>
                        );
                    })}
                </Menu>
            )}
        </Root>
    );
}

const Root = styled.div`
    position: relative;
    flex-shrink: 0;
    min-width: 0;
`;

const Trigger = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    max-width: 220px;
    padding: 0 10px 0 8px;
    border-radius: 10px;
    border: 1px solid rgba(112, 112, 112, 0.15);
    background: ${colors.neutralLight};
    color: ${colors.nightDark};
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: rgba(112, 112, 112, 0.06);
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: 2px;
    }

    /* No celular só o avatar: o nome não cabe ao lado do logo. */
    @media screen and (max-width: ${breakpoints.sm}) {
        padding: 0 8px 0 6px;
        gap: 4px;
    }
`;

const CurrentName = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media screen and (max-width: ${breakpoints.sm}) {
        display: none;
    }
`;

const Menu = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    z-index: 40;
    width: 300px;
    max-width: calc(100vw - 32px);
    padding: 6px;
    border-radius: 14px;
    border: 1px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    box-shadow: 0 12px 32px rgba(26, 26, 26, 0.14);

    /* No celular o gatilho fica no meio do header: o menu ocupa a largura da
       tela em vez de sair pela direita. */
    @media screen and (max-width: ${breakpoints.sm}) {
        position: fixed;
        top: 76px;
        left: 16px;
        right: 16px;
        width: auto;
        max-width: none;
    }
`;

const Option = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    border: 0;
    border-radius: 10px;
    background: transparent;
    text-align: left;
    cursor: pointer;

    &:hover,
    &:focus-visible {
        background: ${panelAccentAlpha(colors.occasionalPurple, '12')};
        outline: none;
    }

    &[aria-checked='true'] {
        background: ${panelAccentAlpha(colors.occasionalPurple, '0F')};
    }
`;

const OptionText = styled.span`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
`;

const OptionName = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    color: ${colors.nightDark};
`;

const OptionHint = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    color: ${colors.night};
`;

const Caption = styled.div`
    margin: 8px 10px 4px;
    padding-top: 8px;
    border-top: 1px solid ${colors.neutralDark};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${colors.nightLight};
`;
